import {
  InlineFormattedNumber,
  isCounterCurrency,
  Text,
  useOrderIncrements,
  useSecurity,
  useStrategiesContext,
} from '@talos/kyoko';
import { useMemo, type ReactNode } from 'react';
import type { PreTradeResponseData } from '../../../redux/preTradeApiTypes';
import { formatStrategyDisplayName } from '../../../utils';
import { MarketsValue } from './MarketsValue';
import { TimeValue } from './TimeValue';

export interface StrategyOverviewRow {
  name: string;
  label: string;
  consideredField?: string;
  labelColor?: 'colorTextNegative' | 'colorTextPositive' | 'colorTextDefault';
  value: ReactNode;
}

/**
 * Returns directional color only for explicit side labels; all other labels are neutral.
 */
export function getSideLabelColor(sideLabel: string): NonNullable<StrategyOverviewRow['labelColor']> {
  if (sideLabel === 'SELL') {
    return 'colorTextNegative';
  }
  if (sideLabel === 'BUY') {
    return 'colorTextPositive';
  }

  return 'colorTextDefault';
}

/**
 * Builds the overview rows rendered in the first block of Strategy Summary.
 */
export function useStrategyOverviewRows(scenario: PreTradeResponseData): StrategyOverviewRow[] {
  const { strategiesByName } = useStrategiesContext();
  const { NewOrderSingle, StartTime, EndTime } = scenario;
  const { Strategy, Side, OrderQty, Price, Parameters, Markets, Symbol, Currency } = NewOrderSingle ?? {};
  const strategyDisplayName = formatStrategyDisplayName(Strategy, strategiesByName);
  const security = useSecurity(Symbol);
  const orderIncrements = useOrderIncrements({ Symbol, Currency });
  const quantityUnit = isCounterCurrency(Currency, security) ? security?.QuoteCurrency : security?.BaseCurrency;
  const duration = Parameters?.Duration;

  return useMemo(() => {
    const sideLabel = String(Side ?? '-').toUpperCase();
    const sideLabelColor = getSideLabelColor(sideLabel);

    return [
      {
        name: 'Strategy',
        label: 'Strategy',
        value: <Text color="colorTextImportant">{strategyDisplayName ?? '-'}</Text>,
      },
      {
        name: 'Side',
        label: sideLabel,
        labelColor: sideLabelColor,
        value: (
          <InlineFormattedNumber
            showDashIfEmpty={true}
            number={OrderQty}
            increment={orderIncrements.quantityIncrement}
            specification={security?.SizeDisplaySpec}
            currency={quantityUnit}
          />
        ),
      },
      {
        name: 'LimitPrice',
        label: 'Limit Price',
        consideredField: 'LimitPrice',
        value: (
          <InlineFormattedNumber
            showDashIfEmpty={true}
            number={Price}
            increment={security?.DefaultPriceIncrement}
            specification={security?.PriceDisplaySpec}
            currency={security?.QuoteCurrency}
          />
        ),
      },
      {
        name: 'Time',
        label: duration ? 'Duration' : 'Time',
        value: <TimeValue duration={duration} startTime={StartTime} endTime={EndTime} />,
      },
      {
        name: 'Markets',
        label: 'Markets',
        consideredField: 'Markets',
        value: <MarketsValue markets={Markets} />,
      },
    ];
  }, [
    OrderQty,
    Price,
    Markets,
    StartTime,
    EndTime,
    strategyDisplayName,
    Side,
    orderIncrements.quantityIncrement,
    security?.SizeDisplaySpec,
    quantityUnit,
    security?.DefaultPriceIncrement,
    security?.PriceDisplaySpec,
    security?.QuoteCurrency,
    duration,
  ]);
}
